import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, CircularProgress, Stack } from '@mui/material'

import { TagApi } from '@trivie/core/src/services/api-objects'
import { FullTag } from '@trivie/ui-web/components/full-tag'
import { Panel } from '@trivie/ui-web/components/panel/panel'
import { Text } from '@trivie/ui-web/components/text'
import { observer } from 'mobx-react-lite'

interface SubscriptionTagsPanelProps {
  tagIds: number[]
}

export const SubscriptionTagsPanel = observer((props: SubscriptionTagsPanelProps) => {
  const { tagIds } = props
  const history = useHistory()
  const [resolvedTags, setResolvedTags] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const tagRequestRef = useRef<any>()

  const cancelRequests = () => {
    if (tagRequestRef.current) {
      tagRequestRef.current.cancel()
    }
  }

  useEffect(() => {
    let stale = false
    cancelRequests()

    if (!tagIds.length) {
      setResolvedTags([])
      setLoading(false)
      return
    }

    setLoading(true)

    const cancelSource = TagApi.getCancelToken()
    tagRequestRef.current = cancelSource

    TagApi.getAllTags(
      'tags',
      false,
      [{ field: 'tag_key' }, { field: 'tag_value' }],
      'list_tags',
      undefined,
      cancelSource.token,
    )
      .then((resp: any) => {
        if (stale) return
        if (resp.ok) {
          const available = resp.data.rows.map((tag: any) => {
            const key = tag.columns.find((c: any) => c.field === 'tag_key')
            const value = tag.columns.find((c: any) => c.field === 'tag_value')
            return { id: tag.id, key: key.value, value: value.value }
          })
          setResolvedTags(available.filter((a: any) => tagIds.includes(a.id)))
        }
        setLoading(false)
      })
      .catch(() => {
        if (!stale) setLoading(false)
      })

    return () => {
      stale = true
      cancelRequests()
      setResolvedTags([])
    }
  }, [JSON.stringify(tagIds)])

  return (
    <Panel title="Tags" fill>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress size={36} sx={{ top: '-12px', position: 'relative' }} />
        </Box>
      ) : resolvedTags.length > 0 ? (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {resolvedTags.map((tag: any) => (
            <Box
              key={tag.id}
              onClick={() => history.push(`/people/tags/${tag.id}/overview`)}
              sx={{ cursor: 'pointer', borderRadius: 1, '&:hover': { filter: 'brightness(0.96)' } }}
            >
              <FullTag tag={tag} />
            </Box>
          ))}
        </Stack>
      ) : (
        <Text component="div" variant="body1" text="No tag data available" sx={EMPTY_TAGS} />
      )}
    </Panel>
  )
})

const EMPTY_TAGS: React.CSSProperties = {
  fontStyle: 'italic',
  color: 'text.secondary',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  justifyContent: 'center',
  alignSelf: 'center',
  paddingBottom: 4,
}
